import { useEffect, useRef } from 'react';
import { useAuth } from 'react-oidc-context';
import { devAuthUser } from '@config/env';
import { ApiError } from '@core/api/client';
import { useApiClient } from '@core/api/useApiClient';
import { clearPendingTermsAcceptance, readPendingTermsAcceptance } from './termsMeta';

/**
 * Mirror the register form's Terms-of-Use tick to the BE (`POST /api/v1/terms/acceptance`) once a
 * session is authenticated (2026-08-20, following legal advice: the acceptance audit — date,
 * version, conditions — must live in the DB, `v2_terms_acceptances`). The tick happens PRE-auth, so
 * `RegisterPage` stashes it in localStorage and this hook drains the stash on the first
 * authenticated session — the same deferred-mirror shape as `useConsentSync`.
 *
 * The stash is cleared ONLY on a successful POST (a failed attempt retries on the next mount);
 * a 400 (unknown/stale version label) also clears — retrying an unpublishable label forever would
 * just spam, and the failure is visible server-side. Best-effort by design: confirming + first
 * signing in on a DIFFERENT device leaves no stash to drain (documented limitation).
 */
export function useTermsAcceptanceSync(): void {
  const api = useApiClient();

  // AuthProvider isn't mounted in dev-auth mode (useAuth would throw); mirror useConsentSync's guard.
  // devAuthUser is a module-level constant, so the conditional hook call order is stable per render.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const isAuthenticated = devAuthUser ? true : useAuth().isAuthenticated;

  const inFlightRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || inFlightRef.current) return;
    const pending = readPendingTermsAcceptance();
    if (!pending) return;
    inFlightRef.current = true;
    void api
      .post<void>('/api/v1/terms/acceptance', {
        version: pending.version,
        clientAcceptedAt: pending.acceptedAt,
      })
      .then(() => clearPendingTermsAcceptance())
      .catch((err: unknown) => {
        // A validation reject (unknown version) is terminal — clear so it doesn't retry forever.
        if (err instanceof ApiError && err.status === 400) clearPendingTermsAcceptance();
        inFlightRef.current = false;
      });
  }, [isAuthenticated, api]);
}
